/**
 * printReceipt — termal yazıcıya ESC/POS direct (Electron + TCP), yoksa HTML fallback
 *
 * Yazıcı config'i şu sırayla aranır:
 *   1. parameter: opts.printerConfig
 *   2. localStorage: 'restaflex_printer_config' (override / Desktop-only)
 *   3. backend: /api/printers (is_default veya ilk network yazıcı, 1dk cache)
 *
 * Print çıkış sırası:
 *   1. window.electronPrint + TCP (network) → ESC/POS binary, silent, hızlı
 *   2. window.electronPrint + sistem (USB/serial driver) → HTML silent print
 *   3. window.open + window.print() → web fallback (dialog açar)
 */

import { printersAPI } from '../services/api';

const PRINTER_CFG_KEY = 'restaflex_printer_config';
let _printerCache = null;
let _printerCacheTime = 0;
const CACHE_TTL = 60_000; // 1 dakika

function readPrinterConfig() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = localStorage.getItem(PRINTER_CFG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function savePrinterConfig(cfg) {
  try {
    if (cfg) localStorage.setItem(PRINTER_CFG_KEY, JSON.stringify(cfg));
    else localStorage.removeItem(PRINTER_CFG_KEY);
  } catch {}
}

function _toCfg(p) {
  if (!p) return null;
  return {
    type: p.connection_type === 'network' ? 'tcp' : 'system',
    connection: p.connection_type,
    ip: p.ip_address,
    port: p.port || 9100,
    name: p.name,
    paperWidth: String(p.paper_width || 80),
    id: p.id,
  };
}

/**
 * Backend yazıcı listesinden ID veya varsayılan/network yazıcısını bul (1dk cache)
 * @param {number|string} [printerId] — verilirse o ID'li yazıcıyı arar (cache by-pass değil; tüm liste cache'lenir)
 */
async function fetchBackendPrinter(printerId) {
  const now = Date.now();
  let printers;
  if (_printerCache !== null && (now - _printerCacheTime) < CACHE_TTL) {
    printers = _printerCache;
  } else {
    try {
      const res = await printersAPI.getAll();
      printers = res.data?.data || res.data || [];
      _printerCache = printers;
      _printerCacheTime = now;
    } catch (err) {
      return null;
    }
  }
  if (!Array.isArray(printers) || printers.length === 0) return null;

  // 1) Spesifik ID istendiyse
  if (printerId) {
    const idStr = String(printerId);
    const byId = printers.find(p => String(p.id) === idStr);
    if (byId) return _toCfg(byId);
  }
  // 2) is_default + network → is_default → ilk network → ilk
  const chosen = printers.find(p => p.is_default && p.connection_type === 'network')
              || printers.find(p => p.is_default)
              || printers.find(p => p.connection_type === 'network')
              || printers[0]
              || null;
  return _toCfg(chosen);
}

/**
 * Cache invalidate (kullanıcı PrinterManagement'tan yazıcı eklediği zaman)
 */
export function invalidatePrinterCache() {
  _printerCache = null;
  _printerCacheTime = 0;
}

/**
 * ESC/POS payload — Electron main.js içindeki buildESCPOS fonksiyonuna geçer
 */
function toEscposPayload({
  restaurantName, restaurantAddress, phone,
  tableName, waiterName, pax, receiptNumber,
  allItems,
  grossTotal, discountAmt, netTotal, vatPct,
  paymentType, cashAmount, cardAmount, cardLastFour, approvalCode,
  paperWidth, receiptFooter, slogan,
}) {
  return {
    restaurantName: restaurantName || 'RestaFlex',
    slogan: slogan || '',
    restaurantAddress: restaurantAddress || '',
    phone: phone || '',
    tableName: tableName || '',
    waiterName: waiterName || '',
    pax: pax || '',
    receiptNumber: receiptNumber || '',
    items: allItems.map(i => ({
      name: i.name,
      qty: i.qty,
      price: i.price,
      subtotal: i.subtotal,
    })),
    grossTotal: parseFloat(grossTotal) || 0,
    discountAmt: parseFloat(discountAmt) || 0,
    netTotal: parseFloat(netTotal) || 0,
    vatPct: parseFloat(vatPct) || 0,
    paymentType: paymentType || 'cash',
    cashAmount: parseFloat(cashAmount) || 0,
    cardAmount: parseFloat(cardAmount) || 0,
    cardLastFour: cardLastFour || '',
    approvalCode: approvalCode || '',
    paperWidth: paperWidth || '80',
    receiptFooter: receiptFooter || '',
  };
}

export async function printReceipt({
  restaurantName, restaurantAddress, phone, slogan,
  tableName, waiterName, pax, receiptNumber,
  orders, grossTotal, discountAmt, netTotal, vatPct,
  paymentType, cashAmount, cardAmount, cardLastFour, approvalCode,
  printerConfig, // explicit override
  printerId,     // backend yazıcı ID'si (PosReceiptSettings → default_receipt_printer_id)
  t, lang,
}) {
  const tx = typeof t === 'function' ? t : (key) => {
    const fallback = {
      cash: 'Nakit', card: 'Kart', total: 'Toplam', quantity: 'Miktar', discount: 'İndirim',
      receiptTitle: 'Adisyon', receiptSubtitle: 'Restoran Yönetim Sistemi',
      receiptSplit: 'Karma (Nakit + Kart)', receiptProduct: 'Ürün', receiptUnit: 'Birim',
      receiptGrandTotal: 'TOPLAM', receiptPaymentLabel: 'Ödeme:',
      receiptThanks: 'Teşekkürler!', receiptThanksLong: 'Bizi tercih ettiğiniz için teşekkür ederiz.',
    };
    return fallback[key] || key;
  };

  // Sepet item'larını flat liste yap
  const allItems = [];
  (orders || []).forEach(order => {
    (order.items || order.order_items || []).forEach(item => {
      allItems.push({
        name: item.item_name || item.name || '-',
        qty: parseInt(item.quantity) || 1,
        price: parseFloat(item.unit_price || item.price) || 0,
        subtotal: (parseInt(item.quantity) || 1) * (parseFloat(item.unit_price || item.price) || 0),
      });
    });
  });

  // Config kaynak öncelik:
  //   1. param printerConfig (explicit override)
  //   2. param printerId varsa → backend listesinden o ID
  //   3. localStorage (Desktop manual override)
  //   4. backend default (is_default, network, vs.)
  let cfg = printerConfig;
  if (!cfg && printerId) cfg = await fetchBackendPrinter(printerId);
  if (!cfg) cfg = readPrinterConfig();
  if (!cfg) cfg = await fetchBackendPrinter();
  const isElectron = typeof window !== 'undefined' && window.electronPrint;

  // ── 1. Electron + TCP yazıcı: ESC/POS direct (en iyi seçenek)
  if (isElectron && cfg && (cfg.type === 'tcp' || cfg.connection === 'network') && cfg.ip) {
    try {
      const payload = toEscposPayload({
        restaurantName, restaurantAddress, phone, slogan,
        tableName, waiterName, pax, receiptNumber,
        allItems,
        grossTotal, discountAmt, netTotal, vatPct,
        paymentType, cashAmount, cardAmount, cardLastFour, approvalCode,
        paperWidth: cfg.paperWidth || cfg.paper_width || '80',
        receiptFooter: cfg.footer || '',
      });
      const result = await window.electronPrint.printTCP(cfg.ip, cfg.port || 9100, payload);
      if (result && result.success === false) {
        throw new Error(result.errorType || 'TCP print failed');
      }
      return { ok: true, method: 'tcp-escpos' };
    } catch (err) {
      console.error('[printReceipt] TCP ESC/POS failed:', err.message);
      // Fallback: sistem yazıcısına HTML
    }
  }

  // ── 2. Electron + sistem yazıcı: HTML silent print
  if (isElectron && cfg && cfg.type === 'system' && cfg.name) {
    try {
      const html = renderHtml({
        tx, lang, restaurantName, tableName, allItems,
        grossTotal, discountAmt, netTotal, paymentType, cashAmount, cardAmount,
      });
      const result = await window.electronPrint.printReceipt(html, cfg.name, true /* silent */);
      if (result && result.success === false) {
        throw new Error(result.errorType || 'System print failed');
      }
      return { ok: true, method: 'system-silent' };
    } catch (err) {
      console.error('[printReceipt] System silent print failed:', err.message);
    }
  }

  // ── 3. Web fallback: yeni pencere + dialog
  const html = renderHtml({
    tx, lang, restaurantName, tableName, allItems,
    grossTotal, discountAmt, netTotal, paymentType, cashAmount, cardAmount,
  });
  if (typeof window === 'undefined') return { ok: false, error: 'No window' };
  const printWindow = window.open('', '_blank', 'width=400,height=600');
  if (!printWindow) return { ok: false, error: 'Popup blocked' };
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 300);
  return { ok: true, method: 'web-fallback' };
}

function renderHtml({ tx, lang, restaurantName, tableName, allItems, grossTotal, discountAmt, netTotal, paymentType, cashAmount, cardAmount }) {
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
  const htmlLang = lang || 'tr';
  const dateLocale = htmlLang === 'ar' ? 'ar' : htmlLang === 'en' ? 'en-US' : 'tr-TR';
  const now = new Date();
  const dateStr = now.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = now.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' });
  const payLabel = paymentType === 'cash' ? tx('cash')
    : paymentType === 'card' ? tx('card')
    : tx('receiptSplit');
  const itemRows = allItems.map(item => `
    <tr><td class="item-name">${esc(item.name)}</td><td class="item-qty">${item.qty}x</td><td class="item-price">₺${item.price.toFixed(2)}</td><td class="item-total">₺${item.subtotal.toFixed(2)}</td></tr>
  `).join('');
  const splitRows = paymentType === 'split' ? `
    <tr class="total-row"><td colspan="3">${tx('cash')}</td><td>₺${parseFloat(cashAmount || 0).toFixed(2)}</td></tr>
    <tr class="total-row"><td colspan="3">${tx('card')}</td><td>₺${parseFloat(cardAmount || 0).toFixed(2)}</td></tr>
  ` : '';
  const discountRow = discountAmt > 0 ? `
    <tr class="total-row discount-row"><td colspan="3">${tx('discount')}</td><td>-₺${parseFloat(discountAmt).toFixed(2)}</td></tr>
  ` : '';
  const htmlDir = htmlLang === 'ar' ? 'rtl' : 'ltr';

  return `<!DOCTYPE html><html lang="${htmlLang}" dir="${htmlDir}"><head><meta charset="UTF-8"/><title>${tx('receiptTitle')} — ${esc(tableName)}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Courier New',Courier,monospace;font-size:13px;color:#000;background:#fff;width:80mm;margin:0 auto;padding:8px}.center{text-align:center}.brand{font-size:22px;font-weight:bold;letter-spacing:2px;margin-bottom:2px}.divider{border-top:1px dashed #000;margin:6px 0}.divider-solid{border-top:2px solid #000;margin:6px 0}table{width:100%;border-collapse:collapse}th{font-size:11px;border-bottom:1px solid #000;padding-bottom:4px;text-align:left}th:last-child,td:last-child{text-align:right}th:nth-child(2),td:nth-child(2){text-align:center;width:28px}th:nth-child(3),td:nth-child(3){text-align:right;width:60px}td{padding:3px 0;font-size:12px;vertical-align:top}.item-name{max-width:120px;word-wrap:break-word}.total-row td{padding:2px 0}.grand-total td{font-size:15px;font-weight:bold;padding-top:4px}.discount-row td{color:#555}.footer{text-align:center;margin-top:8px;font-size:12px}@media print{html,body{width:80mm}@page{margin:4mm;size:80mm auto}}</style>
</head><body><div class="center"><div class="brand">${esc((restaurantName && restaurantName.trim()) || 'RestaFlex')}</div><div style="font-size:11px">${tx('receiptSubtitle')}</div></div><div class="divider"></div>
<div class="center" style="font-size:12px"><div><strong>${esc(tableName)}</strong></div><div>${dateStr} ${timeStr}</div></div><div class="divider"></div>
<table><thead><tr><th>${tx('receiptProduct')}</th><th>${tx('quantity')}</th><th>${tx('receiptUnit')}</th><th>${tx('total')}</th></tr></thead><tbody>${itemRows||'<tr><td colspan="4" style="text-align:center">—</td></tr>'}</tbody></table>
<div class="divider-solid"></div><table><tbody>${discountRow}${splitRows}<tr class="grand-total"><td colspan="3">${tx('receiptGrandTotal')}</td><td>₺${parseFloat(netTotal||0).toFixed(2)}</td></tr></tbody></table>
<div class="divider"></div><div style="font-size:12px;margin:4px 0"><strong>${tx('receiptPaymentLabel')}</strong> ${payLabel}</div><div class="divider"></div>
<div class="footer"><div style="font-size:14px;margin-bottom:4px">${tx('receiptThanks')}</div><div style="font-size:11px">${tx('receiptThanksLong')}</div></div></body></html>`;
}
